// Genera una escala de 11 tonos (50..950) de estilo Tailwind a partir de un
// único color base (ej. el `primaryColor` de un tenant), y la aplica como
// variables CSS en :root para que tailwind.config.js las consuma vía
// rgb(var(--color-primary-500) / <alpha-value>).
//
// Por qué: los colores de marca del tenant (Aludra `appMaster.templateColors`)
// llegan como un único hex, pero Tailwind necesita 11 tonos por paleta. Se
// interpola manteniendo el matiz/saturación del color recibido y variando
// solo la luminosidad en puntos fijos — no es una generación "perceptualmente
// perfecta", pero es suficiente para theming de marca sin agregar dependencias.

type RGB = [number, number, number];

const LIGHTNESS_STOPS: Array<[string, number]> = [
  ['50', 0.97], ['100', 0.93], ['200', 0.84], ['300', 0.72], ['400', 0.58],
  ['500', 0.46], ['600', 0.38], ['700', 0.30], ['800', 0.22], ['900', 0.16], ['950', 0.09],
];

function hexToRgb(hex: string): RGB | null {
  const match = hex.trim().replace(/^#/, '').match(/^([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i);
  if (!match) return null;
  return [parseInt(match[1], 16), parseInt(match[2], 16), parseInt(match[3], 16)];
}

function rgbToHsl([r, g, b]: RGB): [number, number, number] {
  r /= 255; g /= 255; b /= 255;
  const max = Math.max(r, g, b);
  const min = Math.min(r, g, b);
  const l = (max + min) / 2;
  const d = max - min;
  let h = 0;
  let s = 0;
  if (d !== 0) {
    s = d / (1 - Math.abs(2 * l - 1));
    switch (max) {
      case r: h = ((g - b) / d) % 6; break;
      case g: h = (b - r) / d + 2; break;
      default: h = (r - g) / d + 4; break;
    }
    h *= 60;
    if (h < 0) h += 360;
  }
  return [h, s, l];
}

function hslToRgb(h: number, s: number, l: number): RGB {
  const c = (1 - Math.abs(2 * l - 1)) * s;
  const x = c * (1 - Math.abs(((h / 60) % 2) - 1));
  const m = l - c / 2;
  let r = 0, g = 0, b = 0;
  if (h < 60) [r, g, b] = [c, x, 0];
  else if (h < 120) [r, g, b] = [x, c, 0];
  else if (h < 180) [r, g, b] = [0, c, x];
  else if (h < 240) [r, g, b] = [0, x, c];
  else if (h < 300) [r, g, b] = [x, 0, c];
  else [r, g, b] = [c, 0, x];
  return [
    Math.round((r + m) * 255),
    Math.round((g + m) * 255),
    Math.round((b + m) * 255),
  ];
}

/** Retorna un mapa { "50": "234 242 248", ... } listo para CSS var (formato "R G B"). */
export function generateShadeScale(baseHex: string): Record<string, string> | null {
  const rgb = hexToRgb(baseHex);
  if (!rgb) return null;
  const [h, s] = rgbToHsl(rgb);
  const scale: Record<string, string> = {};
  for (const [stop, lightness] of LIGHTNESS_STOPS) {
    const [r, g, b] = hslToRgb(h, s, lightness);
    scale[stop] = `${r} ${g} ${b}`;
  }
  return scale;
}

function applyScale(prefix: string, baseHex: string | undefined | null): void {
  if (!baseHex) return; // sin color de tenant: se conservan las variables por defecto de index.css
  const scale = generateShadeScale(baseHex);
  if (!scale) return; // hex inválido: no rompe nada, solo no aplica override
  const root = document.documentElement;
  for (const [stop, rgb] of Object.entries(scale)) {
    root.style.setProperty(`--color-${prefix}-${stop}`, rgb);
  }
}

/**
 * Aplica los colores de marca del tenant activo a las variables CSS globales.
 * `primaryColor` gobierna la paleta Tailwind `primary` (botones, links, acentos
 * principales). `secondaryColor` gobierna `accent` — NUNCA la paleta Tailwind
 * `secondary`, que es la escala neutra/gris usada en todo el chrome de la UI.
 */
export function applyTenantBrandColors(primaryColor?: string | null, secondaryColor?: string | null): void {
  applyScale('primary', primaryColor);
  applyScale('accent', secondaryColor);
}
